/**
 * Tipos, fetcher y hook para el endpoint /api/sections.
 *
 * El endpoint devuelve las secciones habilitadas del tenant activo (resuelto
 * server-side, no por el cliente). Solo se exponen las que existen en el
 * sectionRegistry — las demás se ignoran silenciosamente.
 */

import { useEffect, useState } from "react";
import { sectionRegistry } from "./registry";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:6787";

export type SectionInfo = {
  key: string;
  sortOrder: number;
  enabled: boolean;
};

type SectionsResponse = {
  sections: SectionInfo[];
};

export type SectionsState =
  | { status: "loading" }
  | { status: "error"; message: string; suspended?: boolean }
  | { status: "ok"; sections: SectionInfo[] };

/** Error lanzado cuando el tenant está suspendido (403 + code TENANT_SUSPENDED). */
export class TenantSuspendedError extends Error {
  readonly code = "TENANT_SUSPENDED" as const;
  constructor() {
    super("Tenant suspended");
    this.name = "TenantSuspendedError";
  }
}

export async function fetchSections(): Promise<SectionInfo[]> {
  const res = await fetch(`${API_URL}/api/sections`, {
    credentials: "include",
  });

  if (!res.ok) {
    if (res.status === 403) {
      // Distinguir suspensión de un 403 normal (sin sesión / sin membresía).
      const payload = (await res.json().catch(() => ({}))) as { code?: string };
      if (payload.code === "TENANT_SUSPENDED") {
        throw new TenantSuspendedError();
      }
    }
    throw new Error(`/api/sections ${res.status}`);
  }

  const body = (await res.json()) as SectionsResponse;
  return body.sections ?? [];
}

/**
 * Hook que consume /api/sections y filtra solo las que existen en el registry.
 * Se dispara cuando hasSession pasa a true (igual que fetchData en App.tsx).
 */
export function useSections(hasSession: boolean): SectionsState {
  const [state, setState] = useState<SectionsState>({ status: "loading" });

  useEffect(() => {
    if (!hasSession) return;

    let cancelled = false;
    setState({ status: "loading" });

    fetchSections()
      .then((sections) => {
        if (cancelled) return;
        // Filtrar las que tienen componente en el registry
        const known = sections.filter((s) => s.key in sectionRegistry);
        setState({ status: "ok", sections: known });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof TenantSuspendedError) {
          setState({ status: "error", message: "TENANT_SUSPENDED", suspended: true });
          return;
        }
        const message =
          err instanceof Error ? err.message : "Error desconocido al cargar secciones";
        console.error("[useSections]", err);
        setState({ status: "error", message });
      });

    return () => {
      cancelled = true;
    };
  }, [hasSession]);

  return state;
}
